/**
 * SignInNudgeModal — fires 60 seconds after first page load for every
 * session where the user is not signed in. Resets each browser session
 * so it re-appears on the next visit until the user creates an account.
 *
 * Intentionally does NOT mention Filmistani PRO or the 7-day trial —
 * the goal is to convert guests to free accounts first.
 */
import { useEffect, useState } from "react";
import {
  X, BookMarked, Sparkles, Star, Gamepad2,
  MessageSquare, Tv2, Check,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { startGoogleLogin } from "../lib/googleAuth";
import GoogleSignInButton from "./GoogleSignInButton";

const SS_KEY = "fm_signin_nudge_modal_shown"; // sessionStorage — resets every session

const BENEFITS = [
  {
    icon: BookMarked,
    label: "Watchlist",
    sub:   "Save movies & shows to watch later",
  },
  {
    icon: Sparkles,
    label: "Personalised feed",
    sub:   "Rails tuned to your taste",
  },
  {
    icon: Star,
    label: "Rate & review",
    sub:   "Track everything you've watched",
  },
  {
    icon: MessageSquare,
    label: "Ask Filmi",
    sub:   "Your AI cinema buddy",
  },
  {
    icon: Gamepad2,
    label: "Games & leaderboard",
    sub:   "Filmi Guess, Box Office & more",
  },
  {
    icon: Tv2,
    label: "Social discussions",
    sub:   "Polls, debates, community",
  },
];

export default function SignInNudgeModal() {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (user?.user_id) return; // already signed in

    // Show once per session only
    try {
      if (sessionStorage.getItem(SS_KEY) === "1") return;
    } catch (_) { return; }

    // Fire after 60 seconds
    const t = setTimeout(() => {
      setOpen(true);
      try { sessionStorage.setItem(SS_KEY, "1"); } catch (_) {}
    }, 60_000);

    return () => clearTimeout(t);
  }, [loading, user?.user_id]);

  if (!open || user?.user_id) return null;

  const handleSignIn = async () => {
    setOpen(false);
    try { await startGoogleLogin(); } catch (_) {}
  };

  const handleDismiss = () => setOpen(false);

  return (
    <div
      className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Sign in to Filmistani — it's free"
      data-testid="signin-nudge-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        onClick={handleDismiss}
      />

      {/* Card */}
      <div className="relative z-10 w-full sm:max-w-[440px] rounded-t-3xl sm:rounded-2xl bg-[#0c0c0c] border border-white/10 shadow-2xl overflow-hidden animate-[slideUp_0.4s_cubic-bezier(0.16,1,0.3,1)]">

        {/* Red accent bar */}
        <div className="h-[3px] bg-gradient-to-r from-[#E50914] via-rose-400 to-[#E50914]" />

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          aria-label="Close"
          data-testid="signin-nudge-modal-dismiss"
          className="absolute top-4 right-4 p-1.5 rounded-full text-neutral-500 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-5 pb-6">

          {/* FREE badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 text-xs font-bold uppercase tracking-widest mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            100% Free — No card needed
          </div>

          {/* Headline */}
          <h2 className="font-display text-[1.6rem] leading-tight font-extrabold tracking-tight text-white mb-1">
            Sign in &amp; unlock everything
          </h2>
          <p className="text-sm text-neutral-400 mb-5 leading-relaxed">
            Create your free Filmistani account in seconds and get access to all of these:
          </p>

          {/* Benefits grid */}
          <div className="grid grid-cols-2 gap-2 mb-5">
            {BENEFITS.map(({ icon: Icon, label, sub }) => (
              <div
                key={label}
                className="flex items-start gap-2 p-3 rounded-xl bg-white/[0.04] border border-white/[0.07] hover:border-white/15 transition-colors"
              >
                <div className="w-6 h-6 rounded-lg bg-[#E50914]/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-3.5 h-3.5 text-[#E50914]" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white leading-tight">{label}</p>
                  <p className="text-[11px] text-neutral-500 leading-snug mt-0.5">{sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* All free callout */}
          <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <Check className="w-4 h-4 text-emerald-400 shrink-0" />
            <p className="text-xs text-neutral-300">
              All features above are <span className="text-white font-semibold">completely free</span> — forever.
            </p>
          </div>

          {/* Sign in CTA */}
          <GoogleSignInButton
            onClick={handleSignIn}
            label="Continue with Google — It's FREE"
            fullWidth
            testid="signin-nudge-modal-cta"
            className="text-[15px] py-3"
          />

          <button
            onClick={handleDismiss}
            data-testid="signin-nudge-modal-later"
            className="w-full text-center text-xs text-neutral-600 hover:text-neutral-400 transition mt-3 py-1"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
